<template>
  <transition name="success-modal-fade">
    <div v-if="isVisible" class="success-modal-overlay">
      <section
        class="success-modal-card"
        role="alertdialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        :aria-describedby="descriptionId"
      >
        <header class="success-modal-header">
          <span class="success-modal-icon" aria-hidden="true">
            <i class="fa-solid fa-check"></i>
          </span>

          <div class="success-modal-heading">
            <h2 :id="titleId">{{ resolvedTitle }}</h2>
            <p>{{ resolvedEyebrow }}</p>
          </div>

          <button
            class="success-modal-close"
            type="button"
            title="Chiudi"
            aria-label="Chiudi messaggio"
            @click="closeModal"
          >
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </header>

        <div :id="descriptionId" class="success-modal-body">
          <p class="success-modal-message">{{ resolvedMessage }}</p>
          <p v-if="supportingMessage" class="success-modal-supporting">
            {{ supportingMessage }}
          </p>
        </div>

        <footer class="success-modal-footer">
          <button
            ref="primaryAction"
            class="success-modal-primary"
            type="button"
            @click="closeModal"
          >
            {{ actionLabel }}
          </button>
        </footer>
      </section>
    </div>
  </transition>
</template>

<script>
export default {
  name: "SuccessMessage",
  props: {
    message: {
      type: String,
      default: "",
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    eyebrow: {
      type: String,
      default: "",
    },
    actionLabel: {
      type: String,
      default: "Continua",
    },
  },
  data() {
    const uid = this._uid || Math.random().toString(36).slice(2);
    return {
      titleId: `success-modal-title-${uid}`,
      descriptionId: `success-modal-description-${uid}`,
    };
  },
  computed: {
    normalizedMessage() {
      return String(this.message || "")
        .trim()
        .toLowerCase();
    },
    isGenericSaveMessage() {
      return /^(tutte le )?modifiche (sono state )?salvate con successo!?$/.test(
        this.normalizedMessage
      );
    },
    presentation() {
      const message = this.normalizedMessage;

      if (this.isGenericSaveMessage || message.includes("modifiche salvate")) {
        return {
          title: "Modifiche salvate",
          eyebrow: "Salvataggio completato",
        };
      }
      if (message.includes("scaricat") || message.includes("download")) {
        return {
          title: "Download completato",
          eyebrow: "File pronto",
        };
      }
      if (message.includes("eliminat")) {
        return {
          title: "Eliminazione completata",
          eyebrow: "Operazione completata",
        };
      }
      if (message.includes("aggiornat") || message.includes("modificat")) {
        return {
          title: "Aggiornamento completato",
          eyebrow: "Modifiche applicate",
        };
      }
      if (message.includes("caricat") || message.includes("sostituit")) {
        return {
          title: "Importazione completata",
          eyebrow: "File acquisito",
        };
      }
      if (message.includes("copiat")) {
        return {
          title: "Copiato",
          eyebrow: "Operazione completata",
        };
      }
      return {
        title: "Operazione completata",
        eyebrow: "Tutto è andato a buon fine",
      };
    },
    resolvedTitle() {
      return this.title || this.presentation.title;
    },
    resolvedEyebrow() {
      return this.eyebrow || this.presentation.eyebrow;
    },
    resolvedMessage() {
      if (this.isGenericSaveMessage) {
        return "Le modifiche sono state salvate correttamente.";
      }
      return this.message;
    },
    supportingMessage() {
      return this.isGenericSaveMessage ? "Puoi continuare a lavorare." : "";
    },
  },
  watch: {
    isVisible: {
      immediate: true,
      handler(isVisible) {
        if (typeof document === "undefined") return;
        if (isVisible) {
          document.addEventListener("keydown", this.handleKeydown);
          this.$nextTick(() => this.$refs.primaryAction?.focus());
        } else {
          document.removeEventListener("keydown", this.handleKeydown);
        }
      },
    },
  },
  beforeDestroy() {
    if (typeof document !== "undefined") {
      document.removeEventListener("keydown", this.handleKeydown);
    }
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    handleKeydown(event) {
      if (event.key === "Escape") this.closeModal();
    },
  },
};
</script>

<style scoped>
.success-modal-overlay {
  position: fixed;
  z-index: 10000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(17, 27, 35, 0.58);
  backdrop-filter: blur(2px);
}

.success-modal-card {
  width: min(34rem, 100%);
  overflow: hidden;
  border: 1px solid rgba(30, 55, 49, 0.14);
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 28px 75px rgba(14, 31, 27, 0.28);
  color: #1d2a31;
}

.success-modal-header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1rem;
  padding: 1.2rem 1.35rem;
  border-bottom: 1px solid #e4ebe8;
  background: linear-gradient(135deg, #ffffff 0%, #f7fbf9 100%);
}

.success-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.35rem;
  height: 3.35rem;
  border: 1px solid #bde8d8;
  border-radius: 50%;
  background: #e7f8f1;
  color: #087f5b;
  box-shadow: inset 0 0 0 5px rgba(255, 255, 255, 0.42);
}

.success-modal-icon i {
  font-size: 1.4rem;
}

.success-modal-heading h2 {
  margin: 0;
  color: #18262d;
  font-size: 1.3rem;
  font-weight: 800;
  line-height: 1.2;
}

.success-modal-heading p {
  margin: 0.24rem 0 0;
  color: #68747d;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.055em;
  text-transform: uppercase;
}

.success-modal-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.1rem;
  height: 2.1rem;
  padding: 0;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #6e7981;
  cursor: pointer;
  transition: background-color 0.18s ease, color 0.18s ease;
}

.success-modal-close:hover,
.success-modal-close:focus-visible {
  outline: none;
  background: #eaf0ed;
  color: #26363d;
}

.success-modal-body {
  padding: 1.55rem 1.75rem 1.65rem;
  text-align: center;
}

.success-modal-message,
.success-modal-supporting {
  margin: 0;
  color: #35434b;
  font-size: 1rem;
  line-height: 1.55;
  white-space: pre-wrap;
}

.success-modal-supporting {
  margin-top: 0.18rem;
  color: #637079;
}

.success-modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 1rem 1.35rem 1.15rem;
  border-top: 1px solid #e4ebe8;
  background: #f8faf9;
}

.success-modal-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-width: 10.5rem;
  min-height: 2.8rem;
  padding: 0.72rem 1.25rem;
  border: 1px solid #087554;
  border-radius: 7px;
  background: linear-gradient(135deg, #11966d, #087554);
  box-shadow: 0 7px 18px rgba(8, 117, 84, 0.2);
  color: #ffffff;
  cursor: pointer;
  font-size: 0.94rem;
  font-weight: 800;
  transition: transform 0.18s ease, box-shadow 0.18s ease,
    background-color 0.18s ease;
}

.success-modal-primary:hover {
  box-shadow: 0 9px 22px rgba(8, 117, 84, 0.27);
  transform: translateY(-1px);
}

.success-modal-primary:focus-visible {
  outline: 3px solid rgba(17, 150, 109, 0.24);
  outline-offset: 3px;
}

.success-modal-fade-enter-active,
.success-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.success-modal-fade-enter-active .success-modal-card,
.success-modal-fade-leave-active .success-modal-card {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.success-modal-fade-enter,
.success-modal-fade-leave-to {
  opacity: 0;
}

.success-modal-fade-enter .success-modal-card,
.success-modal-fade-leave-to .success-modal-card {
  opacity: 0;
  transform: translateY(8px) scale(0.985);
}

@media (max-width: 520px) {
  .success-modal-header {
    gap: 0.75rem;
    padding: 1rem;
  }

  .success-modal-icon {
    width: 2.8rem;
    height: 2.8rem;
  }

  .success-modal-heading h2 {
    font-size: 1.12rem;
  }

  .success-modal-body {
    padding: 1.35rem 1.1rem;
  }

  .success-modal-footer {
    padding: 0.9rem 1rem 1rem;
  }

  .success-modal-primary {
    width: 100%;
  }
}
</style>

<style>
.dm .success-modal-card {
  border-color: #3a4a46;
  background: #202927;
  color: #edf5f2;
}

.dm .success-modal-header {
  border-bottom-color: #3a4a46;
  background: linear-gradient(135deg, #26312e 0%, #202927 100%);
}

.dm .success-modal-heading h2 {
  color: #f1f7f5;
}

.dm .success-modal-heading p,
.dm .success-modal-supporting {
  color: #aebbb7;
}

.dm .success-modal-message {
  color: #dce7e3;
}

.dm .success-modal-footer {
  border-top-color: #3a4a46;
  background: #1b2422;
}

.dm .success-modal-close {
  color: #aebbb7;
}

.dm .success-modal-close:hover,
.dm .success-modal-close:focus-visible {
  background: #34423e;
  color: #ffffff;
}
</style>
